import { fetchJSON as fetchAPI } from './client';

export type DNAScore = {
  key: string; name: string; current: number; historical?: number; benchmark?: number;
  target?: number; gap?: number; priority?: string; trend?: string; percentile?: number;
};

export type DNAMetadata = {
  marketplace: string; sku: string; brand: string; category: string;
  snapshot_count: number; last_sync: string; data_freshness: string; observation_coverage: number;
};

export type DNASummary = {
  asin: string; version: number; overall_score: number; confidence: number;
  top_domains: DNAScore[]; bottom_domains?: DNAScore[]; metadata: DNAMetadata; generated_at: string;
};

export type DNADomain = {
  key: string; name: string; version: number; score: number; status: string;
  freshness?: string; attributes?: Record<string, unknown>; updated_at: string;
};

export type DNAProfile = {
  asin: string; tenant_id: string; version: number; overall_score: number; confidence: number;
  domains: DNADomain[]; scores: DNAScore[]; metadata: DNAMetadata;
  generated_at: string; updated_at: string;
};

export type DNASnapshot = {
  id: string; asin: string; version: number; tag?: string; type: string; overall_score: number; created_at: string;
};

export type DNAVersion = {
  version: number; asin: string; tag?: string; immutable: boolean; created_at: string; reason?: string;
};

export type DNAChange = {
  domain: string; field: string; old_value?: string; new_value?: string; change_type: string;
};

export type DNADiff = {
  asin: string; from_version: number; to_version: number; changes: DNAChange[]; generated_at: string;
};

export type DNABenchmark = {
  asin: string; domain: string; group: string; percentile: number; ranking?: number;
  score: number; benchmark: number; gap: number;
};

export type DNATimelineEvent = {
  id: string; asin: string; type: string; title: string; domain?: string; version?: number; timestamp: string;
};

export type DNASearchResult = {
  key: string; label: string; type: string; asin?: string; path: string;
};

export type DNADomainSpec = {
  key: string; name: string; description?: string; fields?: string[]; status: string; versioned?: boolean;
};

const COMMAND_BASE = import.meta.env.VITE_COMMAND_API ?? '/api/v1/command';
const fetchCmd = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const marketplaceDNAEngineApi = {
  summary: (asin: string) => fetchCmd<DNASummary>(`/dna/${encodeURIComponent(asin)}/summary`),
  detail: (asin: string) => fetchCmd<DNAProfile>(`/dna/${encodeURIComponent(asin)}/detail`),
  history: (asin: string) => fetchCmd<{ history: DNAVersion[] }>(`/dna/${encodeURIComponent(asin)}/history`).then((r) => r.history),
  snapshots: (asin: string) => fetchCmd<{ snapshots: DNASnapshot[] }>(`/dna/${encodeURIComponent(asin)}/snapshots`).then((r) => r.snapshots),
  compare: (asin: string, fromVersion: number, toVersion: number) =>
    fetchCmd<DNADiff>(`/dna/${encodeURIComponent(asin)}/compare`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ from_version: fromVersion, to_version: toVersion }),
    }),
  scores: (asin: string) => fetchCmd<{ scores: DNAScore[] }>(`/dna/${encodeURIComponent(asin)}/scores`).then((r) => r.scores),
  timeline: (asin: string) => fetchCmd<{ timeline: DNATimelineEvent[] }>(`/dna/${encodeURIComponent(asin)}/timeline`).then((r) => r.timeline),
  benchmarks: (asin: string) => fetchCmd<{ benchmarks: DNABenchmark[] }>(`/dna/${encodeURIComponent(asin)}/benchmark`).then((r) => r.benchmarks),
  refreshStatus: (asin: string) => fetchCmd<Record<string, unknown>>(`/dna/${encodeURIComponent(asin)}/refresh-status`),
  search: (query: string) =>
    fetchCmd<{ results: DNASearchResult[] }>('/dna/search', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query }),
    }).then((r) => r.results),
  domains: () => fetchCmd<{ domains: DNADomainSpec[] }>('/dna/domains').then((r) => r.domains),
};
